import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { SetQuery } from 'use-query-params'

import { CandidatesQueryParams } from '../../CandidatesContainer.types'

const UNASSIGNED_OPTION_VALUE = '__unassigned'

export function useAssigneeFilter({
  taskAssignees,
  loadingTaskAssignees,
  urlAssigneeEmails,
  includeUnassigned,
  setQueryParams
}: {
  taskAssignees: Array<{ id: string; email: string }> | undefined
  loadingTaskAssignees: boolean
  urlAssigneeEmails: string[]
  includeUnassigned: boolean
  setQueryParams: SetQuery<CandidatesQueryParams>
}) {
  const { t } = useTranslation()

  const assigneeOptions = useMemo(
    () => [
      ...(taskAssignees ?? []).map(a => ({ label: a.email, value: a.email })),
      { label: t('grants.filters.assignee.unassigned'), value: UNASSIGNED_OPTION_VALUE }
    ],
    [taskAssignees, t]
  )

  const assigneeSelection = useMemo(() => {
    const emailByLower = new Map<string, string>((taskAssignees ?? []).map(a => [a.email.toLowerCase(), a.email]))
    const resolvedSelectedEmails = urlAssigneeEmails
      .map(e => emailByLower.get(e.toLowerCase()))
      .filter((v): v is string => !!v)

    return [...resolvedSelectedEmails, ...(includeUnassigned ? [UNASSIGNED_OPTION_VALUE] : [])]
  }, [taskAssignees, urlAssigneeEmails, includeUnassigned])

  const handleAssigneeFilterChange = useCallback(
    (selectedValues: string[]) => {
      const nextIncludeUnassigned = selectedValues.includes(UNASSIGNED_OPTION_VALUE)
      const nextEmails = selectedValues.filter(v => v !== UNASSIGNED_OPTION_VALUE)
      setQueryParams({
        assigneeEmails: nextEmails.length > 0 ? nextEmails : undefined,
        includeUnassigned: nextIncludeUnassigned ? true : undefined
      })
    },
    [setQueryParams]
  )

  const assigneeFilterReady = !loadingTaskAssignees && taskAssignees != null

  return { assigneeSelection, assigneeOptions, assigneeFilterReady, handleAssigneeFilterChange }
}
